import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Thermometer, Droplet, Activity, Brain, Heart, ChevronRight, Minus } from 'lucide-react';
import { usePeriodStore } from '../../store/periodStore';

const symptoms = [
  { id: 'cramps', name: 'Cramps', icon: <Activity className="w-4 h-4" /> },
  { id: 'headache', name: 'Headache', icon: <Brain className="w-4 h-4" /> },
  { id: 'bloating', name: 'Bloating', icon: <Droplet className="w-4 h-4" /> },
  { id: 'fatigue', name: 'Fatigue', icon: <Activity className="w-4 h-4" /> },
  { id: 'tender_breasts', name: 'Tender Breasts', icon: <Heart className="w-4 h-4" /> },
  { id: 'temperature', name: 'Temperature', icon: <Thermometer className="w-4 h-4" /> }
];

export function SymptomTracker() {
  const { addSymptom, getSymptomsByDate } = usePeriodStore();
  const [selectedSymptom, setSelectedSymptom] = useState<string | null>(null);
  const [intensity, setIntensity] = useState<1 | 2 | 3>(1);
  const [note, setNote] = useState('');

  const handleAddSymptom = () => {
    if (!selectedSymptom) return;

    addSymptom({
      name: selectedSymptom,
      intensity,
      date: new Date().toISOString(),
      notes: note
    });

    setSelectedSymptom(null);
    setIntensity(1);
    setNote('');
  };

  const todaysSymptoms = getSymptomsByDate(new Date().toISOString().split('T')[0]);

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <h2 className="text-lg font-semibold">Log Symptoms</h2>
        
        {/* Symptom Selection */}
        <div className="grid grid-cols-2 gap-2">
          {symptoms.map((symptom) => (
            <motion.button
              key={symptom.id}
              whileTap={{ scale: 0.98 }}
              onClick={() => setSelectedSymptom(symptom.id)}
              className={`theme-card p-4 relative overflow-hidden backdrop-blur-xl border border-border/50 flex items-center justify-between ${
                selectedSymptom === symptom.id ? 'ring-2 ring-primary' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className="relative w-8 h-8 rounded-full bg-muted/50 flex items-center justify-center">
                  {symptom.icon}
                </div>
                <span className="font-medium">{symptom.name}</span>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground" />
            </motion.button>
          ))}
        </div>

        {/* Intensity Selection */}
        {selectedSymptom && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            <h3 className="text-sm font-medium">Intensity</h3>
            <div className="flex gap-2">
              {[1, 2, 3].map((level) => (
                <button
                  key={level}
                  onClick={() => setIntensity(level as 1 | 2 | 3)}
                  className={`flex-1 p-3 rounded-lg transition-colors ${
                    intensity === level
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/70'
                  }`}
                >
                  {level === 1 ? 'Mild' : level === 2 ? 'Moderate' : 'Severe'}
                </button>
              ))}
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Notes (optional)</label>
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="w-full h-24 rounded-lg bg-muted p-3 text-sm resize-none"
                placeholder="Add any additional notes..."
              />
            </div>

            {/* Save Button */}
            <button
              onClick={handleAddSymptom}
              className="w-full theme-button py-3 rounded-lg flex items-center justify-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Save Symptom
            </button>
          </motion.div>
        )}
      </div>

      {/* Today's Symptoms */}
      {todaysSymptoms.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-sm font-medium">Today's Symptoms</h3>
          <div className="space-y-2">
            {todaysSymptoms.map((symptom) => (
              <div
                key={symptom.id}
                className="bg-muted/30 p-3 rounded-lg flex items-center justify-between"
              >
                <div>
                  <div className="font-medium">{symptom.name}</div>
                  <div className="text-sm text-muted-foreground">
                    {symptom.intensity === 1 ? 'Mild' : symptom.intensity === 2 ? 'Moderate' : 'Severe'}
                  </div>
                </div>
                {symptom.notes && (
                  <div className="text-sm text-muted-foreground max-w-[200px] truncate">
                    {symptom.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}